import { whenPageActive } from '@theme/hds-page-activation';
import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent } from '@theme/events';
import { addLinesToCart } from '@theme/cart-batch';

/**
 * SHO-10261: lokale formatter i.p.v. paint-calculator-core — toebehoren staan ook
 * op behang-PDP’s, die de paint-bundle niet mogen laden.
 *
 * @param {number | null | undefined} cents
 * @param {string} [moneyFormat]
 * @returns {string}
 */
function formatMoneyFromCents(cents, moneyFormat = '{{amount}}') {
  if (cents == null || !Number.isFinite(cents)) return '—';

  const value = Math.abs(cents) / 100;
  const [whole, fraction = '00'] = value.toFixed(2).split('.');
  const wholeWithDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const amountComma = `${wholeWithDots},${fraction}`;
  const amount = `${whole}.${fraction}`;

  return moneyFormat
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, wholeWithDots)
    .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${fraction}`)
    .replace(/\{\{\s*amount\s*\}\}/g, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole);
}

/**
 * Aanvullende artikelen / toebehoren per variant (SHO-8348).
 *
 * Toont ÉÉN gecombineerde lijst toebehoren van de geselecteerde variant (alle gekoppelde
 * groepen samengevoegd, gededupliceerd per product). Elk uniek product staat als <template>
 * in de DOM + een variant→{title,items}-map; bij `variant:update` clonen we de juiste product-
 * templates in de live-container.
 *
 * Selectiemodel via vinkje + aantal: aantal 0 / uitgevinkt = niet geselecteerd.
 * Vinkje aan zet aantal op minimaal 1; stepper past aantal aan (0 = uitvinken).
 * Klik op de kaart (behalve stepper / variant-picker) toggelt selectie. Klik op afbeelding/titel
 * vinkt aan én opent de quick-add modal. Per item: inline variantkeuze (pills of dropdowns).
 *
 * Kaarten met chips (één optie, meerdere waarden) zijn multi-select (SHO-9517): elke gekozen
 * chip wordt een eigen regel met eigen aantal en gaat als eigen regel mee in de batched add.
 * De kaart houdt dan de groepsnaam + het groepstotaal; de kaart-stepper vervalt. Kaarten met
 * dropdowns (meerdere opties) blijven één keuze.
 *
 * De geselecteerde toebehoren worden NIET los toegevoegd: ze liften mee op de hoofd-
 * "In winkelwagen" — wanneer het hoofd-product-form zijn succes-`CartAddEvent` (source
 * `product-form-component`) vuurt, of bij configurator-PDP's op `addToCart:success`
 * (ConfiguratorNestedCart omzeilt het theme-form), voegen we alle geselecteerde
 * toebehoren in één batched /cart/add toe.
 *
 * Meting (SHO-9521), via `Shopify.analytics.publish` → Custom Pixel → GTM → GA4:
 * `hds:accessory_exposure` (blok in beeld, 1×/pagina), `hds:accessory_panel_toggle`
 * (uitklappen — het paneel staat standaard dicht), `hds:accessory_select` (aan/uit),
 * `hds:accessory_quickadd_open` en `hds:accessory_add` (de batched add). Elke meelift-regel
 * krijgt daarnaast `_bron: 'toebehoren'` mee, zodat de herkomst ook in de order staat.
 *
 * @extends {Component}
 */
/**
 * @typedef {{ id: string, price: number, available: boolean, options: string[], image?: string, productUrl?: string, title?: string }} VapVariant
 * @typedef {{ options: string[], variants: VapVariant[], fallbackImage?: string, isGroup?: boolean }} VapVariantData
 */

/**
 * Aantal thumbnails in de ingeklapte kop wanneer het blok niets meegeeft. Eén, want
 * een stapel van wisselende breedte laat elke titel op een andere x beginnen; wie dat
 * toch wil zet het per blok hoger via de instelling "Miniaturen in de ingeklapte kop".
 */
const PREVIEW_THUMB_COUNT = 1;

/** Woorden die een titel-staart als los blok inleiden ("… - 95 mm x 5 m"). */
const TITLE_SEPARATORS = new Set(['-', '–', '—', '|', '·', '/']);

/**
 * Herkomst op de orderregel: zo is een toebehoren-verkoop ook buiten GA4 terug te
 * vinden (order, Picqer, marge-analyse). Onderstreepje = verborgen voor de klant.
 * Zelfde patroon als de staalkiezer (`_bron: 'staal-kiezer'`).
 */
const CART_BRON = 'toebehoren';

const MEET_PUBLISH_RETRY_MS = 50;
const MEET_PUBLISH_DEADLINE_MS = 8000;

/**
 * Meet-event naar de Shopify Web Pixels (Custom Pixel → GTM → GA4/BigQuery), met het
 * `hds:`-prefix van de andere theme-events. `Shopify.analytics.publish` staat vaak pas
 * ná de theme-modules klaar, dus wachten we er kort op — zonder retry faalt met name het
 * exposure-event stil. Meten mag winkelen nooit breken: alles binnen try/catch.
 * @param {string} naam
 * @param {Record<string, unknown>} payload
 */
function publiceerMeetEvent(naam, payload) {
  if (document.prerendering) {
    whenPageActive(() => publiceerMeetEvent(naam, payload));
    return;
  }
  const deadline = Date.now() + MEET_PUBLISH_DEADLINE_MS;

  const tick = () => {
    try {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish === 'function') {
        publish(naam, payload);
        return;
      }
    } catch {
      return;
    }
    if (Date.now() > deadline) return;
    setTimeout(tick, MEET_PUBLISH_RETRY_MS);
  };

  tick();
}

/**
 * Producttitel inkorten voor de namen-preview in de kop: alles vóór de eerste
 * " - " is de kern ("Stevig klusmes softgrip - 3 sets afbreekmessen 18 mm").
 * @param {string} raw
 * @returns {string}
 */
function shortenTitle(raw) {
  const [head] = raw.trim().split(' - ');
  return (head ?? '').trim();
}

export class VariantAdditionalProducts extends Component {
  /** @type {Record<string, { title?: string, items: string[], defaultOpen?: boolean }>} */
  #dataByVariantId = {};

  /** @type {Map<string, HTMLTemplateElement>} */
  #templatesByProductId = new Map();

  /** @type {Map<string, VapVariantData>} */
  #variantDataByProductId = new Map();

  /** @type {string | undefined} */
  #currentVariantId = undefined;

  /** @type {(() => void) | undefined} */
  #teardownVariantInput = undefined;

  /** @type {QuickAddComponent | null} */
  #quickAdd = null;

  /** @type {Map<string, string>} */
  #variantChoiceByProductId = new Map();

  /** @type {WeakMap<HTMLElement, VapVariantData>} */
  #variantDataByItem = new WeakMap();

  /**
   * Multi-select groepskaarten (SHO-9517): per kaart de gekozen types met hun aantal.
   * productId → (variantId → aantal). Leeg = niets gekozen.
   * @type {Map<string, Map<string, number>>}
   */
  #linesByProductId = new Map();

  /**
   * Meting (SHO-9521): laatst gemelde selectie, variantId → regelgegevens. `#recompute()`
   * vergelijkt hiermee, zodat elk selectiepad (kaart-klik, vinkje, chip, stepper, toetsenbord)
   * automatisch een select-event geeft zonder overal een aanroep te hoeven zetten.
   * @type {Map<string, { aantal: number, naam: string, prijs_centen: number, groep: boolean }>}
   */
  #selectieSnapshot = new Map();

  /** Meting: uit tijdens het leegmaken ná een geslaagde add — anders regent het 'uit'-events. */
  #meldSelecties = true;

  /** Meting: paneel-toggle door de code (metaobject/blokinstelling) is geen klik van de klant. */
  #panelSyncBezig = false;

  #exposureGemeld = false;

  /** @type {IntersectionObserver | undefined} */
  #exposureObserver = undefined;

  /** Staat de quick-add modal open vanuit dít blok? Bepaalt of we `_bron` meesturen. */
  #quickAddVanuitBlok = false;

  connectedCallback() {
    super.connectedCallback();

    this.#reloadFromDom();
    this.#quickAdd = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
    // `toggle` bubbelt niet, dus in de capture-fase meeluisteren op het details-paneel.
    this.addEventListener('toggle', this.#onPanelToggle, { capture: true });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.addEventListener('addToCart:success', this.#onConfiguratorCartSuccess);
    document.addEventListener('formdata', this.#onFormData);
    this.#bindVariantInputFallback();

    this.#applyVariantId(this.#resolveInitialVariantId());
    this.#observeerExposure();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('toggle', this.#onPanelToggle, { capture: true });
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('addToCart:success', this.#onConfiguratorCartSuccess);
    document.removeEventListener('formdata', this.#onFormData);
    this.#teardownVariantInput?.();
    this.#teardownVariantInput = undefined;
    this.#exposureObserver?.disconnect();
    this.#exposureObserver = undefined;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reloadFromDom();
    this.#quickAdd = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));
    this.#applyVariantId(this.#resolveInitialVariantId());
    this.#observeerExposure();
  }

  /** Herbouw map + product-templates uit de DOM. */
  #reloadFromDom() {
    const payload = this.querySelector('script[type="application/json"].vap__map');
    try {
      const parsed = payload?.textContent ? JSON.parse(payload.textContent) : {};
      this.#dataByVariantId = parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      this.#dataByVariantId = {};
    }

    this.#templatesByProductId = new Map();
    for (const tpl of this.querySelectorAll('template.vap__prod')) {
      const pid = tpl.getAttribute('data-product-id');
      if (pid && tpl instanceof HTMLTemplateElement) this.#templatesByProductId.set(pid, tpl);
    }

    this.#variantDataByProductId = new Map();
    for (const script of this.querySelectorAll('script.vap__variant-data')) {
      const pid = script.getAttribute('data-product-id');
      if (!pid) continue;
      try {
        const parsed = script.textContent ? JSON.parse(script.textContent) : null;
        if (parsed && Array.isArray(parsed.variants)) {
          this.#variantDataByProductId.set(pid, parsed);
        }
      } catch {
        /* ignore invalid variant JSON */
      }
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    if (event.target instanceof Element && event.target.closest('#quick-add-dialog, .quick-add-modal')) {
      return;
    }

    const id = event.detail.resource?.id;
    if (id == null || id === '') return;

    const key = String(id);
    if (!(key in this.#dataByVariantId)) return;

    this.#applyVariantId(key);
  };

  #productFormScope() {
    return (
      this.closest('product-form-component') ??
      this.closest('.product-information')?.querySelector('product-form-component') ??
      this.closest('.product-details')?.querySelector('product-form-component')
    );
  }

  #resolveMainVariantId() {
    const productForm = this.#productFormScope();
    if (productForm instanceof Element) {
      const form = productForm.querySelector('form[action*="/cart/add"]');
      if (form instanceof HTMLFormElement) return this.#resolveMainVariantIdFromForm(form);
    }
    return null;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string | null}
   */
  #resolveMainVariantIdFromForm(form) {
    const variantIdInput =
      form.querySelector('[ref="variantId"]') ?? form.querySelector('input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement && variantIdInput.value) return variantIdInput.value;
    return null;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #isConfiguratorForm(form) {
    if (form.querySelector('input[name="properties[_configurator_config]"]')) return true;
    return Boolean(form.closest('.product-configurator-section'));
  }

  /**
   * @param {HTMLFormElement} form
   */
  #isFormInProductScope(form) {
    const scope = this.#productFormScope();
    return scope instanceof Element && scope.contains(form);
  }

  #resolveInitialVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return fromUrl;

    const fromDataset = this.dataset.initialVariantId;
    if (fromDataset) return fromDataset;

    return this.#resolveMainVariantId();
  }

  #bindVariantInputFallback() {
    const productForm = this.#productFormScope();
    const variantIdInput =
      productForm?.querySelector('[ref="variantId"]') ?? productForm?.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;

    const onChange = () => {
      if (variantIdInput.value) this.#applyVariantId(variantIdInput.value);
    };
    variantIdInput.addEventListener('change', onChange);

    const observer = new MutationObserver(() => {
      if (variantIdInput.value) onChange();
    });
    observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

    this.#teardownVariantInput = () => {
      variantIdInput.removeEventListener('change', onChange);
      observer.disconnect();
    };
  }

  /**
   * Render de gecombineerde toebehoren-lijst van de actieve variant in de live-container.
   * @param {string | null} variantId
   */
  #applyVariantId(variantId) {
    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    const key = variantId ? String(variantId) : '';
    const entry = (key && this.#dataByVariantId[key]) || null;
    const items = entry && Array.isArray(entry.items) ? entry.items : [];

    if (!items.length) {
      this.#currentVariantId = key;
      live.replaceChildren();
      this.hidden = true;
      return;
    }

    // Zelfde variant + al gerenderd → niet herbouwen, zodat de selectie behouden
    // blijft bij spurieuze variant:update's (anders wist replaceChildren selectie/aantallen).
    if (key === this.#currentVariantId && live.childElementCount > 0) {
      this.hidden = false;
      this.#syncPanelOpen(entry);
      const hasVisiblePicker = live.querySelector('[data-vap-variants]:not([hidden])');
      if (!hasVisiblePicker) {
        this.#initVariantPickers(live);
      }
      return;
    }
    this.#currentVariantId = key;

    this.#snapshotVariantChoices(live);
    this.#linesByProductId.clear();
    live.replaceChildren();
    // Andere variant = andere kaarten; de oude selectie is geen 'uit' van de klant.
    this.#selectieSnapshot.clear();
    for (const pid of items) {
      const tpl = this.#templatesByProductId.get(String(pid));
      if (tpl) live.appendChild(tpl.content.cloneNode(true));
    }

    this.hidden = live.childElementCount === 0;
    this.#syncPanelOpen(entry);
    this.#initVariantPickers(live);
    this.#updateSummaryPreview();
    this.#recompute();
  }

  /**
   * Open/dicht van het details-paneel: metaobject `standaard_open` of theme-blokinstelling.
   * @param {{ defaultOpen?: boolean } | null} entry
   */
  #syncPanelOpen(entry) {
    const panel = this.querySelector('[data-vap-panel]');
    if (!(panel instanceof HTMLDetailsElement)) return;

    const blockDefault = this.dataset.expandedDefault === 'true';
    const metaOpen = entry?.defaultOpen === true;
    const open = metaOpen || blockDefault;
    if (panel.open === open) return;

    // De `toggle` die hierop volgt is van ons, niet van de klant. Het event komt als taak
    // binnen, dus de vlag valt pas in de volgende taak weg.
    this.#panelSyncBezig = true;
    panel.open = open;
    setTimeout(() => {
      this.#panelSyncBezig = false;
    }, 0);
  }

  /* ------------------------------------------------------------------ meting (SHO-9521) */

  /**
   * Vaste kern van elke payload: bij welk hoofdproduct/variant stond de klant.
   * @returns {Record<string, unknown>}
   */
  #meetBasis() {
    return {
      hoofdproduct_id: this.dataset.hoofdproductId || '',
      hoofdproduct_titel: this.dataset.hoofdproductTitel || '',
      hoofdvariant_id: this.#currentVariantId || '',
      page_location: window.location.href,
    };
  }

  /**
   * Exposure pas melden als het blok écht in beeld komt — het staat onder de vouw en
   * standaard dicht, dus "aanwezig op de pagina" zou de noemer flink opblazen.
   */
  #observeerExposure() {
    if (this.#exposureGemeld || this.#exposureObserver) return;
    if (typeof IntersectionObserver !== 'function') return;

    // Threshold 0: een uitgeklapt blok is hoger dan het scherm, dus een percentage-drempel
    // zou op mobiel nooit gehaald worden.
    this.#exposureObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#meldExposure();
    });
    this.#exposureObserver.observe(this);
  }

  /** Eén exposure per paginaweergave, met de staat van het paneel op dat moment. */
  #meldExposure() {
    if (this.#exposureGemeld || this.hidden) return;

    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    const kaarten = live.querySelectorAll('[data-vap-item]');
    if (!kaarten.length) return;

    this.#exposureGemeld = true;
    this.#exposureObserver?.disconnect();
    this.#exposureObserver = undefined;

    const panel = this.querySelector('[data-vap-panel]');
    publiceerMeetEvent('hds:accessory_exposure', {
      ...this.#meetBasis(),
      aantal_kaarten: kaarten.length,
      aantal_groepskaarten: live.querySelectorAll('[data-vap-item][data-vap-multi="true"]').length,
      blok_open: panel instanceof HTMLDetailsElement ? panel.open : false,
    });
  }

  /**
   * Open/dicht van het toebehoren-paneel. Zonder dit event is niet te zien hoeveel
   * klanten de lijst überhaupt uitklappen — hij staat standaard dicht.
   * @param {Event} event
   */
  #onPanelToggle = (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLDetailsElement) || !panel.matches('[data-vap-panel]')) return;
    if (this.#panelSyncBezig) return;

    const live = this.querySelector('[data-vap-live]');
    publiceerMeetEvent('hds:accessory_panel_toggle', {
      ...this.#meetBasis(),
      actie: panel.open ? 'open' : 'dicht',
      aantal_kaarten: live instanceof HTMLElement ? live.querySelectorAll('[data-vap-item]').length : 0,
    });

    // Uitklappen ná het scrollen kan het eerste zicht op de kaarten zijn.
    if (panel.open) this.#meldExposure();
  };

  /**
   * Huidige selectie als platte map variantId → regelgegevens; groepskaarten leveren
   * één regel per gekozen type.
   * @param {HTMLElement} live
   * @returns {Map<string, { aantal: number, naam: string, prijs_centen: number, groep: boolean }>}
   */
  #selectieNu(live) {
    /** @type {Map<string, { aantal: number, naam: string, prijs_centen: number, groep: boolean }>} */
    const selectie = new Map();

    for (const item of live.querySelectorAll('[data-vap-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const kaartnaam = item.dataset.productTitle || '';

      if (item.dataset.vapMulti === 'true') {
        const data = this.#variantDataByItem.get(item);
        for (const [variantId, aantal] of this.#itemLines(item)) {
          if (!(aantal > 0)) continue;
          const variant = this.#findVariantById(data ?? { options: [], variants: [] }, variantId);
          selectie.set(String(variantId), {
            aantal,
            naam: variant ? `${kaartnaam} — ${this.#lineLabel(variant)}` : kaartnaam,
            prijs_centen: variant?.price ?? 0,
            groep: true,
          });
        }
        continue;
      }

      const aantal = this.#itemQty(item);
      const variantId = item.dataset.variantId ?? '';
      if (!variantId || !(aantal > 0)) continue;
      selectie.set(variantId, {
        aantal,
        naam: kaartnaam,
        prijs_centen: Number(item.dataset.price) || 0,
        groep: false,
      });
    }

    return selectie;
  }

  /**
   * Vergelijk de selectie met de vorige stand en meld wat erbij kwam of wegviel.
   * @param {HTMLElement} live
   */
  #meetSelectie(live) {
    const nu = this.#selectieNu(live);

    if (this.#meldSelecties) {
      for (const [variantId, regel] of nu) {
        if (this.#selectieSnapshot.has(variantId)) continue;
        this.#meldSelectie('aan', variantId, regel);
      }
      for (const [variantId, regel] of this.#selectieSnapshot) {
        if (nu.has(variantId)) continue;
        this.#meldSelectie('uit', variantId, regel);
      }
    }

    this.#selectieSnapshot = nu;
  }

  /**
   * @param {'aan' | 'uit'} actie
   * @param {string} variantId
   * @param {{ aantal: number, naam: string, prijs_centen: number, groep: boolean }} regel
   */
  #meldSelectie(actie, variantId, regel) {
    publiceerMeetEvent('hds:accessory_select', {
      ...this.#meetBasis(),
      actie,
      variant_id: variantId,
      artikel_naam: regel.naam,
      prijs_centen: regel.prijs_centen,
      is_groepskaart: regel.groep,
      aantal: actie === 'aan' ? regel.aantal : 0,
    });
  }

  /**
   * De batched add zelf: dit is het enige moment waarop toebehoren-omzet aan het element
   * toe te schrijven is. Het standaard `add_to_cart` van de pixel bevat alleen het
   * hoofdproduct-form, niet deze meelift-regels.
   * @param {Array<[string, { aantal: number, naam: string, prijs_centen: number, groep: boolean }]>} regels
   * @param {'meelift' | 'quick-add'} bron
   */
  #meldAdd(regels, bron) {
    if (!regels.length) return;

    let waarde = 0;
    let stuks = 0;
    for (const [, regel] of regels) {
      waarde += regel.prijs_centen * regel.aantal;
      stuks += regel.aantal;
    }

    publiceerMeetEvent('hds:accessory_add', {
      ...this.#meetBasis(),
      bron,
      aantal_regels: regels.length,
      aantal_stuks: stuks,
      waarde_centen: waarde,
      artikelen: regels.map(([variantId, regel]) => ({
        variant_id: variantId,
        artikel_naam: regel.naam,
        aantal: regel.aantal,
        prijs_centen: regel.prijs_centen,
        is_groepskaart: regel.groep,
      })),
    });
  }

  /**
   * Add uit de quick-add modal: één artikel, buiten de batched add om. Zonder dit event
   * zou die verkoop alleen als gewone `add_to_cart` in GA4 staan, zonder herkomst.
   * @param {HTMLElement} live
   * @param {string} variantId
   */
  #meldQuickAddToevoeging(live, variantId) {
    const line = live.querySelector(`[data-vap-line][data-variant-id="${CSS.escape(variantId)}"]`);
    const item = line?.closest('[data-vap-item]') ?? live.querySelector(`[data-vap-item][data-variant-id="${CSS.escape(variantId)}"]`);
    if (!(item instanceof HTMLElement)) return;

    const kaartnaam = item.dataset.productTitle || '';
    const data = this.#variantDataByItem.get(item);
    const variant = this.#findVariantById(data ?? { options: [], variants: [] }, variantId);
    const isGroep = Boolean(line);

    this.#meldAdd(
      [
        [
          variantId,
          {
            aantal: 1,
            naam: isGroep && variant ? `${kaartnaam} — ${this.#lineLabel(variant)}` : kaartnaam,
            prijs_centen: variant?.price ?? (Number(item.dataset.price) || 0),
            groep: isGroep,
          },
        ],
      ],
      'quick-add'
    );
  }

  /**
   * Onthoud inline variantkeuze vóór rebuild van de live-lijst.
   * @param {HTMLElement} live
   */
  #snapshotVariantChoices(live) {
    for (const item of live.querySelectorAll('[data-vap-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const pid = item.dataset.productId;
      const vid = item.dataset.variantId;
      if (pid && vid) this.#variantChoiceByProductId.set(pid, vid);
    }
  }

  /**
   * Initialiseer inline variant-pickers op alle items in scope.
   * @param {ParentNode} scope
   */
  #initVariantPickers(scope) {
    const items = scope.querySelectorAll('[data-vap-item]');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      const data = this.#parseVariantData(item);

      if (!data) {
        const wrap = item.querySelector('[data-vap-variants]');
        if (wrap instanceof HTMLElement) wrap.hidden = true;
        continue;
      }

      if (data.variants.length <= 1) {
        const wrap = item.querySelector('[data-vap-variants]');
        if (wrap instanceof HTMLElement) wrap.hidden = true;
        continue;
      }

      this.#variantDataByItem.set(item, data);
      const mode = this.#buildVariantPicker(item, data);

      if (mode === 'pills') {
        // Multi-select (SHO-9517): niets staat vooraf aan; elke gekozen chip wordt een regel.
        item.dataset.vapMulti = 'true';
        item.classList.add('vap-item--multi');
        this.#setItemTitle(item, this.#groupLabel(data));
        this.#renderLines(item);
        continue;
      }

      const preferredId =
        (item.dataset.productId && this.#variantChoiceByProductId.get(item.dataset.productId)) ||
        item.dataset.variantId;
      const variant = this.#findVariantById(data, preferredId) ?? this.#firstAvailableVariant(data);

      if (variant) this.#applyVariantToItem(item, variant, data, { skipPickerSync: false });
    }
  }

  /**
   * Kaartnaam voor een combined-listing-groep: de gemeenschappelijke noemer van de
   * gekoppelde kindtitels. "Schuurpapier P180 - 95 mm x 5 m - Aluminium oxide" +
   * "Schuurpapier P220 - …" → "Schuurpapier - 95 mm x 5 m - Aluminium oxide".
   *
   * De échte groepsnaam staat op de (unlisted) combined-listing parent, en die relatie
   * bestaat alleen in de Admin API — Liquid ziet 'm niet. Vandaar deze afleiding.
   * @param {VapVariantData} data
   * @returns {string}
   */
  #groupLabel(data) {
    if (!data.isGroup) return '';

    const titles = data.variants.map((variant) => (variant.title ?? '').trim()).filter(Boolean);
    if (titles.length < 2) return titles[0] ?? '';

    const words = titles.map((title) => title.split(/\s+/));
    const shortest = Math.min(...words.map((parts) => parts.length));
    const sameWord = (index) =>
      words.every((parts) => parts[index]?.toLowerCase() === words[0][index]?.toLowerCase());

    let head = 0;
    while (head < shortest && sameWord(head)) head++;

    let tail = 0;
    while (
      tail < shortest - head &&
      words.every(
        (parts) =>
          parts[parts.length - 1 - tail]?.toLowerCase() ===
          words[0][words[0].length - 1 - tail]?.toLowerCase()
      )
    ) {
      tail++;
    }

    const prefix = words[0].slice(0, head);
    // Alleen een staart meenemen die als blok leest ("- 95 mm x 5 m"); losse woorden
    // plakken anders aan de kop vast ("Alabastine gr").
    const tailWords = tail ? words[0].slice(words[0].length - tail) : [];
    const suffix = TITLE_SEPARATORS.has(tailWords[0] ?? '') ? tailWords : [];

    const label = [...prefix, ...suffix]
      .join(' ')
      .replace(/\s*[-–—|·/,]\s*$/, '')
      .trim();

    return label.length >= 3 ? label : '';
  }

  /**
   * Zet de kaartnaam (en alles wat die naam citeert) om naar `title`.
   * @param {HTMLElement} item
   * @param {string} title
   */
  #setItemTitle(item, title) {
    if (!title || item.dataset.productTitle === title) return;
    item.dataset.productTitle = title;

    const nameEl = item.querySelector('.vap-item__name');
    if (nameEl instanceof HTMLElement) {
      nameEl.textContent = title;
      if (nameEl.hasAttribute('aria-label')) nameEl.setAttribute('aria-label', `Bekijk ${title}`);
    }

    const thumb = item.querySelector('.vap-item__thumb');
    if (thumb instanceof HTMLElement && thumb.hasAttribute('aria-label')) {
      thumb.setAttribute('aria-label', `Bekijk ${title}`);
    }

    const checkbox = item.querySelector('[data-vap-check]');
    if (checkbox instanceof HTMLElement) checkbox.setAttribute('aria-label', `Meebestellen: ${title}`);

    const qtyInput = item.querySelector('[data-vap-qty]');
    if (qtyInput instanceof HTMLElement) qtyInput.setAttribute('aria-label', `Aantal ${title}`);
  }

  /**
   * Gekozen regels van een multi-select kaart (wordt aangemaakt als die nog niet bestaat).
   * @param {HTMLElement} item
   * @returns {Map<string, number>}
   */
  #itemLines(item) {
    const pid = item.dataset.productId ?? '';
    let lines = this.#linesByProductId.get(pid);
    if (!lines) {
      lines = new Map();
      this.#linesByProductId.set(pid, lines);
    }
    return lines;
  }

  /**
   * @param {VapVariantData} data
   * @param {string} value
   * @returns {VapVariant | undefined}
   */
  #variantForOptionValue(data, value) {
    if (!value) return undefined;
    return data.variants.find((variant) => variant.options[0] === value);
  }

  /**
   * Regel-label: de optiewaarde ("Muurvuller (sneldrogend)", "5 cm") — kort, en exact
   * wat er op de chip staat. De volledige producttitel zit in de quick-add.
   * @param {VapVariant} variant
   * @returns {string}
   */
  #lineLabel(variant) {
    return variant.options?.[0] || variant.title || '';
  }

  /**
   * @param {HTMLElement} item
   * @param {string} variantId
   */
  #toggleLine(item, variantId) {
    const lines = this.#itemLines(item);
    if (lines.has(variantId)) lines.delete(variantId);
    else lines.set(variantId, 1);
    this.#renderLines(item);
    this.#recompute();
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} lineEl
   * @param {number} delta
   */
  #nudgeLine(item, lineEl, delta) {
    const variantId = lineEl.dataset.variantId ?? '';
    if (!variantId) return;

    const lines = this.#itemLines(item);
    const next = Math.max(0, (lines.get(variantId) ?? 0) + delta);

    if (next === 0) {
      lines.delete(variantId);
      this.#renderLines(item);
    } else {
      lines.set(variantId, next);
      const input = lineEl.querySelector('[data-vap-line-qty]');
      if (input instanceof HTMLInputElement) input.value = String(next);
      this.#refreshGroup(item);
    }
    this.#recompute();
  }

  /**
   * Bouw de regels onder een multi-select kaart en zet de chips in de juiste staat.
   * @param {HTMLElement} item
   */
  #renderLines(item) {
    const data = this.#variantDataByItem.get(item);
    const container = item.querySelector('[data-vap-lines]');
    if (!data || !(container instanceof HTMLElement)) return;

    const lines = this.#itemLines(item);
    const fragment = document.createDocumentFragment();

    for (const [variantId, qty] of lines) {
      const variant = this.#findVariantById(data, variantId);
      if (!variant) continue;
      fragment.appendChild(this.#buildLine(variant, qty));
    }

    container.replaceChildren(fragment);
    container.hidden = lines.size === 0;

    for (const pill of item.querySelectorAll('[data-vap-variant-pill]')) {
      if (!(pill instanceof HTMLButtonElement)) continue;
      const variant = this.#variantForOptionValue(data, pill.dataset.value ?? '');
      const chosen = Boolean(variant && lines.has(String(variant.id)));
      pill.setAttribute('aria-pressed', chosen ? 'true' : 'false');
    }

    this.#refreshGroup(item);
  }

  /**
   * @param {VapVariant} variant
   * @param {number} qty
   * @returns {HTMLLIElement}
   */
  #buildLine(variant, qty) {
    const label = this.#lineLabel(variant);

    const line = document.createElement('li');
    line.className = 'vap-line';
    line.dataset.vapLine = '';
    line.dataset.variantId = String(variant.id);
    if (variant.productUrl) line.dataset.productUrl = variant.productUrl;

    const name = document.createElement('span');
    name.className = 'vap-line__name';
    name.textContent = label;
    name.setAttribute('role', 'button');
    name.setAttribute('tabindex', '0');
    name.setAttribute('aria-label', `Bekijk ${variant.title || label}`);

    const price = document.createElement('span');
    price.className = 'vap-line__price';
    price.textContent = this.#formatMoney(variant.price);

    const stepper = document.createElement('div');
    stepper.className = 'vap-stepper';

    const dec = document.createElement('button');
    dec.type = 'button';
    dec.className = 'vap-stepper__btn';
    dec.dataset.vapLineDec = '';
    dec.setAttribute('aria-label', `Eén ${label} minder`);
    dec.innerHTML = '&minus;';

    const input = document.createElement('input');
    input.className = 'vap-stepper__input';
    input.dataset.vapLineQty = '';
    input.type = 'text';
    input.inputMode = 'numeric';
    input.pattern = '[0-9]*';
    input.value = String(qty);
    input.setAttribute('aria-label', `Aantal ${label}`);

    const inc = document.createElement('button');
    inc.type = 'button';
    inc.className = 'vap-stepper__btn';
    inc.dataset.vapLineInc = '';
    inc.setAttribute('aria-label', `Eén ${label} meer`);
    inc.textContent = '+';

    stepper.append(dec, input, inc);
    line.append(name, price, stepper);
    return line;
  }

  /**
   * Kop van een multi-select kaart: totaalprijs, vinkje, thumbnail en beschikbaarheid
   * volgen de gekozen regels.
   * @param {HTMLElement} item
   */
  #refreshGroup(item) {
    const data = this.#variantDataByItem.get(item);
    if (!data) return;

    const lines = this.#itemLines(item);
    let totalCents = 0;
    let units = 0;
    /** @type {VapVariant | undefined} */
    let primary;

    for (const [variantId, qty] of lines) {
      const variant = this.#findVariantById(data, variantId);
      if (!variant || qty <= 0) continue;
      if (!primary) primary = variant;
      totalCents += variant.price * qty;
      units += qty;
    }

    const shown = primary ?? this.#firstAvailableVariant(data);
    const available = data.variants.some((variant) => variant.available);

    item.dataset.available = available ? 'true' : 'false';
    item.classList.toggle('vap-item--unavailable', !available);
    if (shown) {
      item.dataset.variantId = String(shown.id);
      item.dataset.price = String(shown.price);
      if (shown.productUrl) item.dataset.productUrl = shown.productUrl;
    }

    const checkbox = item.querySelector('[data-vap-check]');
    if (checkbox instanceof HTMLInputElement) checkbox.checked = units > 0;

    const unitEl = item.querySelector('.vap-item__unit');
    if (unitEl instanceof HTMLElement) {
      if (!available) unitEl.textContent = 'Niet leverbaar';
      else if (units > 0) unitEl.textContent = this.#formatMoney(totalCents);
      else unitEl.textContent = this.#fromPriceLabel(data, item);
    }

    const imageUrl = shown?.image || data.fallbackImage || '';
    const thumb = item.querySelector('.vap-item__thumb');
    if (thumb instanceof HTMLElement && imageUrl) {
      let img = thumb.querySelector('img');
      if (!(img instanceof HTMLImageElement)) {
        img = document.createElement('img');
        img.loading = 'lazy';
        img.alt = '';
        thumb.replaceChildren(img);
      }
      img.src = imageUrl;
      img.removeAttribute('srcset');
    }
  }

  /**
   * Catalogus-eenheid van dit toebehoren; block-setting alleen als fallback.
   * @param {HTMLElement} [item]
   * @returns {string}
   */
  #itemUnitLabel(item) {
    return (item?.dataset.unitLabel || this.dataset.unitLabel || 'stuk').trim();
  }

  /**
   * "vanaf € 8,50 per stuk" — of zonder "vanaf" als alle types even duur zijn.
   * @param {VapVariantData} data
   * @param {HTMLElement} [item]
   * @returns {string}
   */
  #fromPriceLabel(data, item) {
    const unitLabel = this.#itemUnitLabel(item);
    const prices = data.variants.filter((variant) => variant.available).map((variant) => variant.price);
    if (!prices.length) return 'Niet leverbaar';

    const min = Math.min(...prices);
    const prefix = Math.max(...prices) > min ? 'vanaf ' : '';
    return `${prefix}${this.#formatMoney(min)} per ${unitLabel}`;
  }

  /**
   * @param {HTMLElement} item
   * @returns {VapVariantData | null}
   */
  #parseVariantData(item) {
    const pid = item.dataset.productId;
    if (pid && this.#variantDataByProductId.has(pid)) {
      return this.#variantDataByProductId.get(pid) ?? null;
    }

    // Fallback: legacy script in gekloonde item (oude theme-versie)
    const script = item.querySelector('script[data-vap-variants]');
    if (!script?.textContent) {
      return null;
    }
    try {
      const parsed = JSON.parse(script.textContent);
      if (!parsed || !Array.isArray(parsed.variants)) return null;
      return /** @type {VapVariantData} */ (parsed);
    } catch {
      return null;
    }
  }

  /**
   * @param {VapVariantData} data
   * @returns {number}
   */
  #effectiveOptionCount(data) {
    if (data.options?.length) return data.options.length;
    return Math.max(0, ...data.variants.map((v) => v.options?.length ?? 0));
  }

  /**
   * @param {VapVariantData} data
   * @param {string | undefined} id
   * @returns {VapVariant | undefined}
   */
  #findVariantById(data, id) {
    if (!id) return undefined;
    return data.variants.find((v) => String(v.id) === String(id));
  }

  /**
   * @param {VapVariantData} data
   * @returns {VapVariant | undefined}
   */
  #firstAvailableVariant(data) {
    return data.variants.find((v) => v.available) ?? data.variants[0];
  }

  /**
   * Langste gemeenschappelijke suffix over alle optiewaarden (case-insensitive).
   * @param {string[]} values
   * @returns {{ display: string, raw: string }}
   */
  #detectOptionSuffix(values) {
    if (!values || values.length < 2) return { display: '', raw: '' };

    const trimmed = values.map((v) => String(v).trim());
    const minLen = Math.min(...trimmed.map((s) => s.length));

    for (let len = minLen; len > 0; len--) {
      const rawSuffix = trimmed[0].slice(-len);
      // Alleen een suffix op woordgrens ("15 x 29 cm" → " cm"). Zonder deze eis knipt
      // een toevallig gedeeld teken door de waarden heen: P120/P180/P220 → "P12"+"0",
      // "46 m x 65 cm (30 m²)" → "46 m x 65 cm (3" + "0 m²)".
      if (!/^\s/.test(rawSuffix)) continue;
      const lower = rawSuffix.toLowerCase();
      if (!trimmed.every((s) => s.toLowerCase().endsWith(lower))) continue;

      const display = rawSuffix.trim();
      if (!display) continue;

      const stripped = trimmed.map((s) => this.#stripOptionSuffix(s, rawSuffix));
      if (!stripped.every((s) => s.length > 0)) continue;
      // Geen haakje halverwege afknippen: "46 m x 65 cm (30 m²)" min " m²)" laat "(30" over.
      if (!stripped.every((s) => (s.match(/\(/g) ?? []).length === (s.match(/\)/g) ?? []).length)) continue;

      return { display, raw: rawSuffix };
    }

    return { display: '', raw: '' };
  }

  /**
   * @param {string} value
   * @param {string} rawSuffix
   * @returns {string}
   */
  #stripOptionSuffix(value, rawSuffix) {
    const v = String(value).trim();
    const s = String(rawSuffix);
    if (!s) return v;
    if (v.toLowerCase().endsWith(s.toLowerCase())) {
      return v.slice(0, -s.length).trimEnd();
    }
    return v;
  }

  /**
   * @param {string} value
   * @param {string} rawSuffix
   * @returns {string}
   */
  #formatOptionDisplay(value, rawSuffix) {
    if (!rawSuffix) return value;
    const stripped = this.#stripOptionSuffix(value, rawSuffix);
    return stripped || value;
  }

  /**
   * @param {HTMLElement} item
   * @param {VapVariantData} data
   * @returns {'pills' | 'selects' | 'none'}
   */
  #buildVariantPicker(item, data) {
    const wrap = item.querySelector('[data-vap-variants]');
    if (!(wrap instanceof HTMLElement)) {
      return 'none';
    }

    wrap.replaceChildren();
    wrap.hidden = false;

    const optionCount = this.#effectiveOptionCount(data);
    if (optionCount <= 1) {
      wrap.classList.remove('vap-item__variants--selects');
      const values = this.#uniqueOptionValues(data, 0);
      if (!values.length) {
        wrap.hidden = true;
        return 'none';
      }

      const label = data.options?.[0] ?? 'Variant';
      const suffixInfo = this.#detectOptionSuffix(values);
      wrap.dataset.vapOptionSuffixRaw = suffixInfo.raw;

      const labelEl = document.createElement('span');
      labelEl.className = 'vap-item__variant-label';
      labelEl.textContent = label;

      const pillsWrap = document.createElement('div');
      pillsWrap.className = 'vap-item__variant-pills';
      pillsWrap.dataset.vapVariantPills = '';

      for (const value of values) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vap-item__variant-pill';
        btn.dataset.vapVariantPill = '';
        btn.dataset.optionIndex = '0';
        btn.dataset.value = value;
        btn.textContent = this.#formatOptionDisplay(value, suffixInfo.raw);
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', `${label}: ${value}`);
        const hasAvailable = data.variants.some((v) => v.options[0] === value && v.available);
        if (!hasAvailable) btn.disabled = true;
        pillsWrap.appendChild(btn);
      }

      const suffixEl = document.createElement('span');
      suffixEl.className = 'vap-item__variant-suffix';
      suffixEl.dataset.vapVariantSuffix = '';
      if (suffixInfo.display) {
        suffixEl.textContent = suffixInfo.display;
      } else {
        suffixEl.hidden = true;
      }

      const valuesWrap = document.createElement('div');
      valuesWrap.className = 'vap-item__variant-values';
      valuesWrap.append(pillsWrap, suffixEl);

      const row = document.createElement('div');
      row.className = 'vap-item__variant-row';
      row.append(labelEl, valuesWrap);

      wrap.append(row);

      return 'pills';
    }

    wrap.classList.add('vap-item__variants--selects');
    wrap.removeAttribute('data-vap-option-suffix-raw');
    for (let i = 0; i < optionCount; i++) {
      const optionLabel = data.options?.[i] ?? `Optie ${i + 1}`;
      const values = this.#uniqueOptionValues(data, i);
      const suffixInfo = this.#detectOptionSuffix(values);

      const row = document.createElement('div');
      row.className = 'vap-item__variant-select-row';

      const labelEl = document.createElement('span');
      labelEl.className = 'vap-item__variant-label';
      labelEl.textContent = optionLabel;

      const select = document.createElement('select');
      select.className = 'vap-item__variant-select';
      select.dataset.vapVariantSelect = '';
      select.dataset.optionIndex = String(i);
      select.setAttribute('aria-label', optionLabel);

      for (const value of values) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = this.#formatOptionDisplay(value, suffixInfo.raw);
        select.appendChild(option);
      }

      row.append(labelEl, select);
      if (suffixInfo.display) {
        const suffixEl = document.createElement('span');
        suffixEl.className = 'vap-item__variant-suffix';
        suffixEl.textContent = suffixInfo.display;
        row.appendChild(suffixEl);
      }

      wrap.appendChild(row);
    }
    return 'selects';
  }

  /**
   * @param {VapVariantData} data
   * @param {number} optionIndex
   * @returns {string[]}
   */
  #uniqueOptionValues(data, optionIndex) {
    const seen = new Set();
    /** @type {string[]} */
    const values = [];
    for (const variant of data.variants) {
      const value = variant.options[optionIndex];
      if (!value || seen.has(value)) continue;
      seen.add(value);
      values.push(value);
    }
    return values;
  }

  /**
   * @param {VapVariantData} data
   * @param {string[]} selectedOptions
   * @returns {VapVariant | undefined}
   */
  #resolveVariant(data, selectedOptions) {
    const candidates = data.variants.filter((variant) =>
      selectedOptions.every((value, index) => !value || variant.options[index] === value)
    );
    if (!candidates.length) return this.#firstAvailableVariant(data);
    return candidates.find((v) => v.available) ?? candidates[0];
  }

  /**
   * @param {HTMLElement} item
   * @returns {string[]}
   */
  #readSelectedOptions(item) {
    const data = this.#variantDataByItem.get(item);
    const optionCount = data?.options?.length ?? 0;
    /** @type {string[]} */
    const selected = new Array(optionCount).fill('');

    const pill = item.querySelector('[data-vap-variant-pill][aria-pressed="true"]');
    if (pill instanceof HTMLButtonElement) {
      const index = parseInt(pill.dataset.optionIndex ?? '0', 10);
      selected[index] = pill.dataset.value ?? '';
      return selected;
    }

    for (const select of item.querySelectorAll('[data-vap-variant-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const index = parseInt(select.dataset.optionIndex ?? '0', 10);
      selected[index] = select.value;
    }

    return selected;
  }

  /**
   * @param {HTMLElement} item
   * @param {VapVariant} variant
   * @param {VapVariantData} data
   * @param {{ skipPickerSync?: boolean }} [options]
   */
  #applyVariantToItem(item, variant, data, options = {}) {
    const available = Boolean(variant.available);
    item.dataset.variantId = String(variant.id);
    item.dataset.price = String(variant.price);
    item.dataset.available = available ? 'true' : 'false';
    if (variant.productUrl) {
      item.dataset.productUrl = variant.productUrl;
    }
    item.classList.toggle('vap-item--unavailable', !available);

    const unitLabel = this.#itemUnitLabel(item);
    const unitEl = item.querySelector('.vap-item__unit');
    if (unitEl instanceof HTMLElement) {
      unitEl.textContent = available
        ? `${this.#formatMoney(variant.price)} per ${unitLabel}`
        : 'Niet leverbaar';
    }

    const imageUrl = variant.image || data.fallbackImage || '';
    const thumb = item.querySelector('.vap-item__thumb');
    if (thumb instanceof HTMLElement) {
      let img = thumb.querySelector('img');
      if (imageUrl) {
        if (!(img instanceof HTMLImageElement)) {
          img = document.createElement('img');
          img.loading = 'lazy';
          img.alt = '';
          thumb.replaceChildren(img);
        }
        img.src = imageUrl;
        img.removeAttribute('srcset');
      } else if (img) {
        img.remove();
      }

      if (available) {
        thumb.setAttribute('role', 'button');
        thumb.setAttribute('tabindex', '0');
        thumb.removeAttribute('aria-hidden');
        const title = item.dataset.productTitle ?? '';
        thumb.setAttribute('aria-label', `Bekijk ${title}`);
      } else {
        thumb.removeAttribute('role');
        thumb.removeAttribute('tabindex');
        thumb.setAttribute('aria-hidden', 'true');
        thumb.removeAttribute('aria-label');
      }
    }

    const nameEl = item.querySelector('.vap-item__name');
    if (nameEl instanceof HTMLElement) {
      if (available) {
        nameEl.setAttribute('role', 'button');
        nameEl.setAttribute('tabindex', '0');
        const title = item.dataset.productTitle ?? '';
        nameEl.setAttribute('aria-label', `Bekijk ${title}`);
      } else {
        nameEl.removeAttribute('role');
        nameEl.removeAttribute('tabindex');
        nameEl.removeAttribute('aria-label');
      }
    }

    if (!available && this.#itemQty(item) > 0) {
      this.#setItemSelected(item, false);
    }

    if (!options.skipPickerSync) {
      this.#syncPickerUi(item, variant, data);
    }

    const pid = item.dataset.productId;
    if (pid) this.#variantChoiceByProductId.set(pid, String(variant.id));

    this.#recompute();
  }

  /**
   * @param {HTMLElement} item
   * @param {VapVariant} variant
   * @param {VapVariantData} data
   */
  #syncPickerUi(item, variant, data) {
    const wrap = item.querySelector('[data-vap-variants]');
    const rawSuffix = wrap instanceof HTMLElement ? wrap.dataset.vapOptionSuffixRaw ?? '' : '';
    const labelEl = item.querySelector('.vap-item__variant-label');
    const label = labelEl instanceof HTMLElement ? labelEl.textContent ?? 'Variant' : 'Variant';

    for (const pill of item.querySelectorAll('[data-vap-variant-pill]')) {
      if (!(pill instanceof HTMLButtonElement)) continue;
      const index = parseInt(pill.dataset.optionIndex ?? '0', 10);
      const fullValue = pill.dataset.value ?? '';
      const pressed = variant.options[index] === fullValue;
      pill.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      pill.textContent = this.#formatOptionDisplay(fullValue, rawSuffix);
      pill.setAttribute('aria-label', `${label}: ${fullValue}`);
    }

    for (const select of item.querySelectorAll('[data-vap-variant-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const index = parseInt(select.dataset.optionIndex ?? '0', 10);
      const value = variant.options[index];
      if (value && [...select.options].some((opt) => opt.value === value)) {
        select.value = value;
      }
    }

    if (this.#effectiveOptionCount(data) > 1) {
      this.#refreshSelectOptions(item, data);
    }
  }

  /**
   * Ververs beschikbaarheid van opties in multi-select pickers.
   * @param {HTMLElement} item
   * @param {VapVariantData} data
   */
  #refreshSelectOptions(item, data) {
    const selected = this.#readSelectedOptions(item);

    for (const select of item.querySelectorAll('[data-vap-variant-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const index = parseInt(select.dataset.optionIndex ?? '0', 10);
      const current = select.value;

      for (const option of select.options) {
        const trial = [...selected];
        while (trial.length <= index) trial.push('');
        trial[index] = option.value;
        const match = this.#resolveVariant(data, trial);
        option.disabled = !match?.available;
      }

      if (current) select.value = current;
    }
  }

  /**
   * Vul de ingeklapte kop, zodat zichtbaar is wát er in het paneel zit:
   * titel + namen van de items + de eerste paar productthumbnails.
   */
  #updateSummaryPreview() {
    const live = this.querySelector('[data-vap-live]');
    const sub = this.querySelector('[data-vap-sub]');
    if (!(live instanceof HTMLElement) || !(sub instanceof HTMLElement)) return;

    const items = [...live.querySelectorAll('[data-vap-item]')];
    sub.textContent = items.length ? 'Kies toebehoren' : '';

    const names = this.querySelector('[data-vap-names]');
    if (names instanceof HTMLElement) {
      names.textContent = items
        .map((item) => shortenTitle(item.querySelector('.vap-item__name')?.textContent ?? ''))
        .filter(Boolean)
        .join(', ');
    }

    const thumbs = this.querySelector('[data-vap-thumbs]');
    if (thumbs instanceof HTMLElement) {
      const configured = Number(this.dataset.previewThumbCount);
      const max = Number.isFinite(configured) && this.dataset.previewThumbCount !== '' ? configured : PREVIEW_THUMB_COUNT;
      thumbs.replaceChildren(
        ...items.slice(0, max).flatMap((item) => {
          const src = item.querySelector('.vap-item__thumb img')?.getAttribute('src');
          if (!src) return [];
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          return [img];
        })
      );
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-vap-lines]')) {
      event.stopPropagation();

      const line = target.closest('[data-vap-line]');
      const item = line?.closest('[data-vap-item]');
      if (!(line instanceof HTMLElement) || !(item instanceof HTMLElement)) return;

      if (target.closest('[data-vap-line-inc]')) {
        event.preventDefault();
        this.#nudgeLine(item, line, +1);
      } else if (target.closest('[data-vap-line-dec]')) {
        event.preventDefault();
        this.#nudgeLine(item, line, -1);
      } else if (target.closest('.vap-line__name')) {
        event.preventDefault();
        this.#openQuickAddForLine(item, line);
      }
      return;
    }

    if (target.closest('[data-vap-variants]')) {
      event.stopPropagation();

      const pill = target.closest('[data-vap-variant-pill]');
      if (pill instanceof HTMLButtonElement && !pill.disabled) {
        event.preventDefault();
        this.#onVariantPillClick(pill);
      }
      return;
    }

    if (target.closest('[data-vap-inc]')) {
      this.#nudgeQty(target, +1);
      return;
    }
    if (target.closest('[data-vap-dec]')) {
      this.#nudgeQty(target, -1);
      return;
    }

    if (target.closest('.vap-item__check') || target.closest('[data-vap-check]')) {
      event.stopPropagation();
      return;
    }

    if (target.closest('[data-vap-stepper]')) return;

    const thumbOrName = target.closest('.vap-item__thumb, .vap-item__name');
    if (thumbOrName) {
      const item = thumbOrName.closest('[data-vap-item]');
      if (item instanceof HTMLElement && item.dataset.available === 'true') {
        event.preventDefault();
        this.#selectItem(item);
        this.#openQuickAddForItem(item);
      }
      return;
    }

    const item = target.closest('[data-vap-item]');
    if (item instanceof HTMLElement && item.dataset.available === 'true') {
      this.#toggleItemSelection(item);
    }
  };

  /**
   * Zet item geselecteerd (vinkje aan, aantal minimaal 1) zonder te togglen.
   * @param {HTMLElement} item
   */
  #selectItem(item) {
    if (item.dataset.vapMulti === 'true') {
      if (!this.#itemLines(item).size) this.#selectFirstAvailableLine(item);
      return;
    }

    const checkbox = item.querySelector('[data-vap-check]');
    const qtyInput = item.querySelector('[data-vap-qty]');
    if (!(checkbox instanceof HTMLInputElement) || !(qtyInput instanceof HTMLInputElement)) return;

    const wasSelected = checkbox.checked && this.#rawQty(qtyInput) > 0;
    if (!checkbox.checked) {
      checkbox.checked = true;
      if (this.#rawQty(qtyInput) === 0) qtyInput.value = '1';
      this.#recompute();
    } else if (this.#rawQty(qtyInput) === 0) {
      qtyInput.value = '1';
      this.#recompute();
    }

    if (!wasSelected) checkbox.blur();
  }

  /**
   * Kies het eerste leverbare type van een groep (klik op kaart/vinkje zonder keuze).
   * @param {HTMLElement} item
   */
  #selectFirstAvailableLine(item) {
    const data = this.#variantDataByItem.get(item);
    const first = data?.variants.find((variant) => variant.available);
    if (!first) return;

    this.#itemLines(item).set(String(first.id), 1);
    this.#renderLines(item);
    this.#recompute();
  }

  /**
   * @param {HTMLElement} item
   */
  async #openQuickAddForItem(item) {
    await this.#openQuickAdd({
      url: item.dataset.productUrl,
      variantId: item.dataset.variantId,
      title: item.dataset.productTitle,
      description: item.dataset.productDescription,
      bron: 'kaart',
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} line
   */
  async #openQuickAddForLine(item, line) {
    const data = this.#variantDataByItem.get(item);
    const variant = this.#findVariantById(data ?? { options: [], variants: [] }, line.dataset.variantId);

    await this.#openQuickAdd({
      url: line.dataset.productUrl || item.dataset.productUrl,
      variantId: line.dataset.variantId,
      title: variant?.title || line.querySelector('.vap-line__name')?.textContent || item.dataset.productTitle,
      description: item.dataset.productDescription,
      bron: 'groepsregel',
    });
  }

  /**
   * @param {{ url?: string, variantId?: string, title?: string | null, description?: string, bron?: string }} options
   */
  async #openQuickAdd({ url, variantId, title, description, bron }) {
    if (!this.#quickAdd || !url) return;

    publiceerMeetEvent('hds:accessory_quickadd_open', {
      ...this.#meetBasis(),
      variant_id: variantId || '',
      artikel_naam: title || '',
      bron: bron || 'kaart',
    });

    this.#markeerQuickAddHerkomst();

    await this.#quickAdd.openForProduct({
      url,
      variantId,
      title: title ?? undefined,
      description,
    });
  }

  /**
   * Ook een add uit de quick-add modal is een toebehoren-verkoop. De modal is gedeeld en
   * wordt bij elke variantwissel opnieuw gemorphd, dus een verborgen veld overleeft dat niet:
   * we haken aan op `formdata` (bubbelt vanaf het form) en zetten `_bron` op het moment van
   * versturen. De vlag valt weg zodra de dialog sluit.
   */
  #markeerQuickAddHerkomst() {
    this.#quickAddVanuitBlok = true;

    const dialog = document.getElementById('quick-add-dialog');
    dialog?.addEventListener(
      'dialog:close',
      () => {
        this.#quickAddVanuitBlok = false;
      },
      { once: true }
    );
  }

  /** @param {Event} event */
  #onFormData = (event) => {
    if (!this.#quickAddVanuitBlok) return;
    if (!(event instanceof FormDataEvent)) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('#quick-add-modal-content')) return;

    event.formData.append('properties[_bron]', CART_BRON);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const isEnterOrSpace = event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';

    if (target.matches('.vap-line__name')) {
      const line = target.closest('[data-vap-line]');
      const owner = line?.closest('[data-vap-item]');
      if (line instanceof HTMLElement && owner instanceof HTMLElement && isEnterOrSpace) {
        event.preventDefault();
        this.#openQuickAddForLine(owner, line);
      }
      return;
    }

    if (!target.matches('.vap-item__thumb, .vap-item__name')) return;

    const item = target.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement) || item.dataset.available !== 'true') return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.#selectItem(item);
      this.#openQuickAddForItem(item);
    }
  };

  /**
   * Toggle selectie via vinkje + aantal (0/1).
   * @param {HTMLElement} item
   */
  #toggleItemSelection(item) {
    if (item.dataset.vapMulti === 'true') {
      const lines = this.#itemLines(item);
      if (lines.size) {
        lines.clear();
        this.#renderLines(item);
        this.#recompute();
      } else {
        this.#selectFirstAvailableLine(item);
      }
      return;
    }

    const checkbox = item.querySelector('[data-vap-check]');
    if (!(checkbox instanceof HTMLInputElement)) return;
    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    checkbox.blur();
  }

  /**
   * @param {HTMLButtonElement} pill
   */
  #onVariantPillClick(pill) {
    const item = pill.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement)) return;

    const data = this.#variantDataByItem.get(item);
    if (!data) return;

    if (item.dataset.vapMulti === 'true') {
      const variant = this.#variantForOptionValue(data, pill.dataset.value ?? '');
      if (variant?.available) this.#toggleLine(item, String(variant.id));
      return;
    }

    const index = parseInt(pill.dataset.optionIndex ?? '0', 10);
    const selected = this.#readSelectedOptions(item);
    while (selected.length < data.options.length) selected.push('');
    selected[index] = pill.dataset.value ?? '';

    const variant = this.#resolveVariant(data, selected);
    if (variant) this.#applyVariantToItem(item, variant, data);
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #onVariantSelectChange(select) {
    const item = select.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement)) return;

    const data = this.#variantDataByItem.get(item);
    if (!data) return;

    const selected = this.#readSelectedOptions(item);
    const variant = this.#resolveVariant(data, selected);
    if (variant) this.#applyVariantToItem(item, variant, data);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.matches('[data-vap-variant-select]')) {
      event.stopPropagation();
      this.#onVariantSelectChange(target);
      return;
    }

    if (target instanceof HTMLInputElement && target.matches('[data-vap-line-qty]')) {
      const line = target.closest('[data-vap-line]');
      const owner = line?.closest('[data-vap-item]');
      if (!(line instanceof HTMLElement) || !(owner instanceof HTMLElement)) return;

      // Leeg of 0 ingetypt en dan weggeklikt: de regel verdwijnt (chip gaat weer uit).
      if ((parseInt(target.value, 10) || 0) <= 0) {
        this.#itemLines(owner).delete(line.dataset.variantId ?? '');
        this.#renderLines(owner);
        this.#recompute();
      }
      return;
    }

    if (!(target instanceof HTMLInputElement) || !target.matches('[data-vap-check]')) return;

    const item = target.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement)) return;

    if (item.dataset.vapMulti === 'true') {
      if (!target.checked) {
        this.#itemLines(item).clear();
        this.#renderLines(item);
      } else if (!this.#itemLines(item).size) {
        this.#selectFirstAvailableLine(item);
      }
      this.#recompute();
      return;
    }

    const qtyInput = item.querySelector('[data-vap-qty]');
    if (!(qtyInput instanceof HTMLInputElement)) return;

    if (target.checked) {
      if (this.#rawQty(qtyInput) === 0) qtyInput.value = '1';
    } else {
      qtyInput.value = '0';
    }
    this.#recompute();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-vap-line-qty]')) {
      target.value = target.value.replace(/[^\d]/g, '');

      const line = target.closest('[data-vap-line]');
      const owner = line?.closest('[data-vap-item]');
      if (!(line instanceof HTMLElement) || !(owner instanceof HTMLElement)) return;

      const variantId = line.dataset.variantId ?? '';
      if (variantId) this.#itemLines(owner).set(variantId, Math.max(0, parseInt(target.value, 10) || 0));
      this.#refreshGroup(owner);
      this.#recompute();
      return;
    }

    if (!target.matches('[data-vap-qty]')) return;

    target.value = target.value.replace(/[^\d]/g, '');
    const item = target.closest('[data-vap-item]');
    if (item instanceof HTMLElement) this.#syncCheckboxFromQty(item);
    this.#recompute();
  };

  /**
   * @param {HTMLElement} item
   * @param {boolean} selected
   */
  #setItemSelected(item, selected) {
    if (item.dataset.vapMulti === 'true') {
      if (!selected) {
        this.#itemLines(item).clear();
        this.#renderLines(item);
      }
      return;
    }

    const qtyInput = item.querySelector('[data-vap-qty]');
    const checkbox = item.querySelector('[data-vap-check]');
    if (qtyInput instanceof HTMLInputElement) qtyInput.value = selected ? '1' : '0';
    if (checkbox instanceof HTMLInputElement) checkbox.checked = selected;
  }

  /**
   * @param {HTMLElement} item
   */
  #syncCheckboxFromQty(item) {
    const qtyInput = item.querySelector('[data-vap-qty]');
    const checkbox = item.querySelector('[data-vap-check]');
    if (!(qtyInput instanceof HTMLInputElement) || !(checkbox instanceof HTMLInputElement)) return;
    checkbox.checked = this.#rawQty(qtyInput) > 0;
  }

  /**
   * @param {Element} fromEl
   * @param {number} delta
   */
  #nudgeQty(fromEl, delta) {
    const stepper = fromEl.closest('[data-vap-stepper]');
    const input = stepper?.querySelector('[data-vap-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(Math.max(0, this.#rawQty(input) + delta));
    const item = input.closest('[data-vap-item]');
    if (item instanceof HTMLElement) this.#syncCheckboxFromQty(item);
    this.#recompute();
  }

  /** @param {HTMLInputElement} input */
  #rawQty(input) {
    return Math.max(0, parseInt(input.value, 10) || 0);
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #itemQty(item) {
    if (item.dataset.available !== 'true') return 0;

    if (item.dataset.vapMulti === 'true') {
      let total = 0;
      for (const qty of this.#itemLines(item).values()) total += Math.max(0, qty);
      return total;
    }

    const input = item.querySelector('[data-vap-qty]');
    return input instanceof HTMLInputElement ? this.#rawQty(input) : 0;
  }

  /**
   * Geselecteerde (aantal ≥ 1), leverbare items uit de live-container.
   * @param {ParentNode} scope
   * @returns {HTMLElement[]}
   */
  #selectedItems(scope) {
    return /** @type {HTMLElement[]} */ (
      [...scope.querySelectorAll('[data-vap-item]')].filter(
        (el) => el instanceof HTMLElement && this.#itemQty(el) > 0
      )
    );
  }

  /** Werk de geselecteerde-staat (class), de kop-telling en de footer-hint bij. */
  #recompute() {
    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    let count = 0;
    let totalCents = 0;
    for (const item of live.querySelectorAll('[data-vap-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const qty = this.#itemQty(item);
      const on = qty > 0;
      item.classList.toggle('vap-item--selected', on);
      if (!on) continue;

      // Groepskaart: elk gekozen type telt als eigen keuze, met zijn eigen prijs.
      if (item.dataset.vapMulti === 'true') {
        const data = this.#variantDataByItem.get(item);
        for (const [variantId, lineQty] of this.#itemLines(item)) {
          const variant = data ? this.#findVariantById(data, variantId) : undefined;
          if (!variant || lineQty <= 0) continue;
          count += 1;
          totalCents += variant.price * lineQty;
        }
        continue;
      }

      count += 1;
      totalCents += (parseInt(item.dataset.price ?? '0', 10) || 0) * qty;
    }

    const selectedEl = this.querySelector('[data-vap-selected]');
    if (selectedEl instanceof HTMLElement) {
      if (count) {
        const label = count === 1 ? '1 gekozen' : `${count} gekozen`;
        selectedEl.textContent = `${label} · ${this.#formatMoney(totalCents)}`;
        selectedEl.hidden = false;
      } else {
        selectedEl.textContent = '';
        selectedEl.hidden = true;
      }
    }

    this.#meetSelectie(live);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return formatMoneyFromCents(cents, this.dataset.moneyFormat || '{{amount}}').replace(',00', ',-');
  }

  /**
   * Voeg geselecteerde toebehoren toe na succesvolle hoofd-add.
   * @returns {Promise<boolean>}
   */
  async #batchSelectedAccessoriesToCart() {
    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return false;

    const selected = this.#selectedItems(live);
    if (!selected.length) return false;

    /** @type {Array<{ id: string, quantity: number }>} */
    const items = [];
    for (const item of selected) {
      // Groepskaart: elk gekozen type is een eigen regel in de mand.
      if (item.dataset.vapMulti === 'true') {
        for (const [variantId, qty] of this.#itemLines(item)) {
          if (variantId && qty > 0) items.push({ id: variantId, quantity: qty });
        }
        continue;
      }

      const id = item.dataset.variantId ?? '';
      const quantity = this.#itemQty(item);
      if (id && quantity > 0) items.push({ id, quantity });
    }
    if (!items.length) return false;

    // Regelgegevens vóór het leegmaken vastleggen; ná de reset zijn ze weg.
    const gemeten = [...this.#selectieSnapshot.entries()];

    try {
      // Samen met de kiezers en cadeaus in één verzoek (cart-batch.js): niets valt weg, één verversing.
      await addLinesToCart('variant-additional-products', items.map((line) => ({ ...line, properties: { _bron: CART_BRON } })));
      this.#meldAdd(gemeten, 'meelift');

      // Het leegmaken is geen keuze van de klant → geen 'uit'-events.
      this.#meldSelecties = false;
      for (const item of selected) this.#setItemSelected(item, false);
      this.#recompute();
      this.#meldSelecties = true;
      return true;
    } catch (error) {
      console.error('Aanvullende artikelen — meenemen bij In winkelwagen mislukt:', error);
      return false;
    }
  }

  /**
   * CartAddEvent: batch bij hoofd-PDP add, uitvinken bij quick-add add voor VAP-item.
   * @param {Event} event
   */
  #onCartAdd = async (event) => {
    if (this.hidden) return;
    if (!(event instanceof CartAddEvent)) return;

    const data = event.detail?.data ?? {};
    if (data.didError) return;
    if (data.source !== 'product-form-component') return;

    const addedVariantId = String(event.detail.sourceId ?? data.variantId ?? '');
    if (!addedVariantId) return;

    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    const isQuickAddModal =
      event.target instanceof Element &&
      Boolean(event.target.closest('.quick-add-modal, #quick-add-dialog'));

    if (isQuickAddModal) {
      this.#meldQuickAddToevoeging(live, addedVariantId);

      // Het artikel zit al in de mand; het uitvinken is geen keuze van de klant.
      this.#meldSelecties = false;

      // Groepsregel: alleen díe regel vervalt, de rest van de groep blijft staan.
      const line = live.querySelector(`[data-vap-line][data-variant-id="${CSS.escape(addedVariantId)}"]`);
      const lineOwner = line?.closest('[data-vap-item]');
      if (lineOwner instanceof HTMLElement) {
        this.#itemLines(lineOwner).delete(addedVariantId);
        this.#renderLines(lineOwner);
        this.#recompute();
        this.#meldSelecties = true;
        return;
      }

      const vapItem = live.querySelector(`[data-vap-item][data-variant-id="${CSS.escape(addedVariantId)}"]`);
      if (vapItem instanceof HTMLElement) {
        this.#setItemSelected(vapItem, false);
        this.#recompute();
      }
      this.#meldSelecties = true;
      return;
    }

    const mainVariantId = this.#resolveMainVariantId();
    if (!mainVariantId || addedVariantId !== String(mainVariantId)) return;

    await this.#batchSelectedAccessoriesToCart();
  };

  /**
   * Configurator-PDP: batch na addToCart:success (ConfiguratorNestedCart omzeilt product-form CartAddEvent).
   * @param {Event} event
   */
  #onConfiguratorCartSuccess = async (event) => {
    if (this.hidden) return;

    const form = event.detail?.form;
    if (!(form instanceof HTMLFormElement)) return;
    if (!this.#isConfiguratorForm(form)) return;
    if (!this.#isFormInProductScope(form)) return;
    if (form.closest('.quick-add-modal, #quick-add-dialog')) return;

    const addedVariantId = this.#resolveMainVariantIdFromForm(form);
    if (!addedVariantId) return;

    const mainVariantId = this.#resolveMainVariantId();
    if (!mainVariantId || addedVariantId !== String(mainVariantId)) return;

    await this.#batchSelectedAccessoriesToCart();
  };

}

if (!customElements.get('variant-additional-products')) {
  customElements.define('variant-additional-products', VariantAdditionalProducts);
}
